import React, {
  useState, useContext, useCallback, useMemo
} from 'react';
import { string, bool } from 'prop-types';
import classNames from 'classnames';

import { ProductPod } from '@thd-olt-component-react/product-pod';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Col } from '@thd-olt-component-react/grid';

import { PromoQuantity } from '../promo-quantity/PromoQuantity';
import { usePromoModel } from '../../contexts/PromoModelProvider';
import { usePromoCart } from '../../contexts/PromoCartProvider';
import { usePromoPresentation } from '../../contexts/PromoPresentationProvider';
import {
  buildUpdateCartRequest, generatePromoCartItemsObj, buildAddToCartRequest
} from '../../utils/promo-cart-utils';
import { PromoQuickview } from '../promo-quickview/PromoQuickview';
import { PromoSelectCta } from '../promo-select-cta/PromoSelectCta';
import { PromoWarningMessage } from './subcomponents/PromoWarningMessage';
import './PromoPod.scss';

export const PromoPod = ({ itemId, selected, displayPosition, isInPromoDrawer }) => {
  const {
    isBogo, isMsb, allItemIds, products, storeId, storeZip
  } = usePromoModel();
  const {
    promoCartItemsObj,
    setBogoSelectedProduct,
    freeItemInCart,
    CartModel,
    addToCart,
    updateCart,
    isCartLoading,
  } = usePromoCart();

  const { channel } = useContext(ExperienceContext);
  const { isAppleMobileDevice } = usePromoPresentation();
  const [isQuickviewOpen, setIsQuickviewOpen] = useState(false);
  const [renderQuickview, setRenderQuickview] = useState(false);
  const [quantityWarningText, setQuantityWarningText] = useState('');

  const product = products.get(itemId);
  const inCartQuantity = promoCartItemsObj?.[itemId]?.quantity || 0;
  const showQuantityWarningState = !!quantityWarningText;

  const onSelect = useCallback(() => {
    if (freeItemInCart && freeItemInCart.itemId !== product.itemId) {
      const store = { storeZip, storeId };
      const { CartRequest: updateCartRequest } = buildUpdateCartRequest({
        promoItemModel: promoCartItemsObj[freeItemInCart.itemId],
        desiredQuantity: 0,
        store
      });

      const updatePromise = updateCartRequest.itemDetails.length
        ? updateCart(updateCartRequest)
        : Promise.resolve(CartModel);

      updatePromise
        .then((newCartModel) => {
          const promoCartItems = generatePromoCartItemsObj({ itemModels: newCartModel?.itemModels, allItemIds });

          const { CartRequest: addToCartRequest } = buildAddToCartRequest({
            desiredQuantity: 1,
            promoItemModel: promoCartItems[product.itemId],
            channel,
            product,
            store
          });

          if (addToCartRequest.itemDetails.length) return addToCart(addToCartRequest);

          return Promise.resolve();
        });
    } else {
      setBogoSelectedProduct(!selected ? { ...product, displayPosition } : null);
    }
  }, [
    freeItemInCart,
    product,
    promoCartItemsObj,
    storeZip,
    storeId,
    updateCart,
    allItemIds,
    channel,
    addToCart,
    setBogoSelectedProduct,
    selected,
    displayPosition,
    CartModel
  ]);

  const loadQuickview = useCallback((event) => {
    if (event) event.preventDefault();
    setRenderQuickview(true);
  }, [setRenderQuickview]);

  const openQuickview = useCallback((event) => {
    if (event) event.preventDefault();
    setRenderQuickview(true);
    setIsQuickviewOpen(true);
  }, [setRenderQuickview, setIsQuickviewOpen]);

  const callToActionSection = useMemo(() => {
    return (
      <div style={{ display: 'flex' }}>
        <Col fallback={3} nopadding>
          <>
            {!isBogo && quantityWarningText && <PromoWarningMessage message={quantityWarningText} />}
          </>
        </Col>
        <Col fallback={8} className="promo-pod-cta">
          {isBogo ? (
            <PromoSelectCta
              onSelect={onSelect}
              selected={selected}
              dark={!selected}
            />
          ) : (
            <PromoQuantity
              itemId={itemId}
              setWarningText={setQuantityWarningText}
              showWarningState={showQuantityWarningState}
            />
          )}
        </Col>
      </div>
    );
  }, [isBogo, itemId, onSelect, selected, showQuantityWarningState, quantityWarningText]);

  const miniOptions = useMemo(() => {
    return {
      disabled: isCartLoading,
      enableATC: isMsb && !inCartQuantity,
      onlyShowIfFulfillable: true,
      silentATC: true,
      inCartQuantity,
      onAction: openQuickview,
      callToActionSection,
    };
  }, [isCartLoading, isMsb, inCartQuantity, openQuickview, callToActionSection]);

  const promoPodClasses = classNames('promo-pod', {
    'promo-pod--selected': selected,
    'promo-pod--buybox': !isInPromoDrawer,
  });

  if (!product) return null;

  return (
    <>
      <section
        className={promoPodClasses}
        id={`promo-pod-${itemId}`}
        data-testid={`promo-pod-${itemId}`}
        role="button"
        tabIndex={0}
        onMouseEnter={!isAppleMobileDevice ? loadQuickview : undefined}
      >
        <ProductPod
          key={itemId}
          itemId={itemId}
          titleMaxLine={2}
          podType="mini"
          miniOptions={miniOptions}
          storeId={storeId}
          onClick={openQuickview}
          showDisabledATC
          ignoreAnalytics
        />
      </section>

      { renderQuickview && (
        <PromoQuickview
          isQuickviewOpen={isQuickviewOpen}
          setIsQuickviewOpen={setIsQuickviewOpen}
          product={product}
          storeId={storeId}
          onSelect={onSelect}
          selected={selected}
          isInPromoDrawer={isInPromoDrawer}
          setQuantityWarningText={setQuantityWarningText}
          showQuantityWarningState={showQuantityWarningState}
        />
      )}
    </>
  );
};

PromoPod.displayName = 'PromoPod';

PromoPod.propTypes = {
  itemId: string.isRequired,
  selected: bool,
  displayPosition: string.isRequired,
  isInPromoDrawer: bool,
};

PromoPod.defaultProps = {
  selected: false,
  isInPromoDrawer: true,
};
